import { useEffect, useState } from 'react';
import { Star, MessageSquare, Quote } from 'lucide-react';
import { getGoogleReviews } from '@/services/googleReviews';
import { Review } from '@/data/reviews';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import Autoplay from "embla-carousel-autoplay";

const ReviewsSection = () => {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const data = await getGoogleReviews();
        setReviews(data);
      } catch (error) {
        console.error("Failed to load reviews", error);
      } finally {
        setLoading(false);
      }
    };

    fetchReviews();
  }, []);

  const renderStars = (rating: number) => {
    return [...Array(5)].map((_, index) => (
      <Star
        key={index}
        className={`w-4 h-4 ${index < rating
          ? 'text-yellow-500 fill-yellow-500'
          : 'text-gray-300'
          }`}
      />
    ));
  };

  return (
    <section className="py-12 relative overflow-hidden bg-gray-50">
      {/* Background Elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-10 w-64 h-64 bg-blue-200/20 rounded-full blur-[100px]" />
        <div className="absolute bottom-1/4 right-10 w-64 h-64 bg-purple-200/20 rounded-full blur-[100px]" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-0 space-y-4">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 border border-blue-100 text-blue-600 text-sm font-medium">
            <MessageSquare className="w-4 h-4" />
            <span>Client Testimonials</span>
          </div>

          <h2 className="text-3xl md:text-5xl font-bold text-gray-900">
            What Our Customers <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">
              Say About Us
            </span>
          </h2>

          <p className="text-gray-600 text-lg">
            Don't just take our word for it. Here's what our travelers experienced while exploring with Friends On Road Cab.
          </p>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-64 rounded-2xl bg-gray-200 animate-pulse" />
            ))}
          </div>
        ) : (
          <Carousel
            opts={{
              align: "start",
              loop: true,
            }}
            plugins={[
              Autoplay({
                delay: 4000,
              }),
            ]}
            className="w-full max-w-6xl mx-auto"
          >
            <CarouselContent className="py-12">
              {reviews.map((review, index) => (
                <CarouselItem key={index} className="md:basis-1/2 lg:basis-1/3 pl-6">
                  <div className="h-full pt-4">
                    <div className="bg-white p-6 rounded-[2rem] relative shadow-sm hover:shadow-md transition-shadow duration-300 border border-gray-100 h-full">
                      {/* Floating Rating Pill */}
                      <div className="absolute -top-5 right-8 bg-white px-4 py-2 rounded-full shadow-lg border border-gray-100 flex items-center gap-1 z-10">
                        {renderStars(review.rating)}
                      </div>

                      <div className="flex flex-row gap-4 sm:gap-6 h-full items-start">
                        {/* Avatar & Timestamp Column */}
                        <div className="flex flex-col items-center gap-3 shrink-0">
                          <div className="w-20 h-20 sm:w-28 sm:h-28 rounded-2xl overflow-hidden bg-gray-100">
                            <img
                              src={review.profile_photo_url || "https://ui-avatars.com/api/?name=" + review.author_name}
                              alt={review.author_name}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <span className="text-gray-400 text-[10px] sm:text-xs font-medium uppercase tracking-wider text-center w-full leading-tight line-clamp-2">{review.relative_time_description}</span>
                        </div>

                        {/* Content */}
                        <div className="flex-1 text-left flex flex-col h-full">
                          <h4 className="text-gray-900 text-xl sm:text-[1.2rem] sm:leading-[1rem] font-bold mb-2 sm:mb-3 leading-tight">{review.author_name}</h4>

                          <p className="text-gray-600 leading-relaxed text-sm mb-4 line-clamp-4 flex-grow">
                            {review.text}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            <div className="hidden md:block">
              <CarouselPrevious className="-left-4 lg:-left-12 h-12 w-12 border-blue-100 hover:bg-blue-50 hover:text-blue-600" />
              <CarouselNext className="-right-4 lg:-right-12 h-12 w-12 border-blue-100 hover:bg-blue-50 hover:text-blue-600" />
            </div>

            {/* Mobile Navigation Dots could be added here if needed, but standard swipe works well */}
          </Carousel>
        )}

      </div>
    </section>
  );
};

export default ReviewsSection;
